import type { ChangeEvent, MutableRefObject } from 'react'
import { Tabs } from 'antd'
import { X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import type { Position, TimeRange } from '../types'
import { ScheduleListPanel } from '../../components/ScheduleListPanel'

interface PositionDetailPanelProps {
  robotId: string
  activeTab: string
  positionPanelTab: 'info' | 'schedule'
  selectedPosition: Position | null
  loadingSchedulePositionId: string
  tabsClassName: string
  editingPositionNameRef: MutableRefObject<{
    id: string
    name: string
  } | null>
  isEditable?: boolean
  isAddmode?: boolean
  isHomePlacementRequired?: boolean
  homeYaw?: number

  onClose?: () => void
  onTabChange: (tab: 'info' | 'schedule') => void
  onPositionNameChange: (value: string) => void
  onSchedulesChange: () => void
  onLocalTimeRangesChange?: (ranges: TimeRange[]) => void
  formatYawLabel: (yaw: number) => string
  onSave?: () => void
  onCancelEdit?: () => void
  onEditHomeDirection?: () => void
}

export function PositionDetailPanel({
  robotId,
  activeTab,
  positionPanelTab,
  selectedPosition,
  loadingSchedulePositionId,
  tabsClassName,
  editingPositionNameRef,
  isEditable = false,
  isAddmode = false,
  isHomePlacementRequired = false,
  homeYaw = 0,
  onClose,
  onTabChange,
  onPositionNameChange,
  onSchedulesChange,
  onLocalTimeRangesChange,
  formatYawLabel,
  onSave,
  onCancelEdit,
  onEditHomeDirection,
}: PositionDetailPanelProps) {
  const isHomeFlow = isHomePlacementRequired || selectedPosition?.id === 'home'
  const activePanelTab = isHomeFlow ? 'info' : positionPanelTab
  const showScheduleTab = !isHomeFlow
  const derivedTabsClassName = isHomeFlow
    ? `${tabsClassName} [&_.ant-tabs-tab]:!px-0 [&_.ant-tabs-tab-btn]:!px-0 [&_.ant-tabs-tab]:!py-3`
    : `${tabsClassName} [&_.ant-tabs-tab]:!px-4 [&_.ant-tabs-tab]:!py-3`

  // Tính toán góc Yaw hiển thị = Yaw vị trí (độ) + Yaw của Home
  const getCalculatedYaw = () => {
    if (
      !selectedPosition ||
      typeof selectedPosition.yaw !== 'number' ||
      Number.isNaN(selectedPosition.yaw)
    ) {
      return null
    }
    if (selectedPosition.id === 'home') {
      return selectedPosition.yaw
    }
    const positionYawDeg = selectedPosition.yaw * (180 / Math.PI)
    return positionYawDeg + homeYaw
  }

  const calculatedYaw = getCalculatedYaw()

  return (
    <div className="relative flex w-full shrink-0 flex-col justify-between rounded-2xl ring-1 ring-gray-300 bg-white p-4 shadow-sm min-h-[600px] xl:w-[420px]">
      {onClose && (
        <Button
          onClick={onClose}
          className="absolute right-3 text-[#CE7A58] top-4 z-10 rounded-full transition-colors outline-none"
          variant="ghost"
        >
          <X className="h-5 w-5" />
        </Button>
      )}

      <div className="flex-1">
        <Tabs
          activeKey={activePanelTab}
          animated={false}
          onChange={(key) => {
            if (showScheduleTab) {
              onTabChange(key as 'info' | 'schedule')
            }
          }}
          className={`${derivedTabsClassName} ${!isHomeFlow ? '[&_.ant-tabs-nav]:pr-8' : ''} [&_.ant-tabs-tab]:text-[15px] [&_.ant-tabs-tab]:font-semibold [&_.ant-tabs-nav-wrap]:w-full [&_.ant-tabs-nav-list]:!flex [&_.ant-tabs-nav-list]:!w-full [&_.ant-tabs-tab]:!m-0 [&_.ant-tabs-tab]:!flex-1 [&_.ant-tabs-tab]:!justify-center [&_.ant-tabs-tab+.ant-tabs-tab]:!ml-0 [&_.ant-tabs-nav]:!mb-4`}
          items={[
            {
              key: 'info',
              label: 'Thông tin',
              children:
                !selectedPosition && !isAddmode ? (
                  <div className="rounded-xl border bg-[#f6f7fb] p-4 text-center text-gray-400 text-sm">
                    Chưa có vị trí nào
                  </div>
                ) : (
                  <div className="flex flex-col gap-4">
                    {/* Tiêu đề riêng cho chế độ Thêm mới */}
                    {isAddmode && (
                      <h3 className="text-[18px] font-semibold text-gray-800 mb-1">
                        Thêm vị trí mới
                      </h3>
                    )}

                    {/* Tên vị trí */}
                    <div className="flex flex-col gap-1.5">
                      <label
                        htmlFor="position-name-input"
                        className="text-sm font-medium text-gray-700"
                      >
                        Tên vị trí <span className="text-red-500">*</span>
                      </label>
                      <Input
                        id="position-name-input"
                        value={selectedPosition?.name || ''}
                        placeholder="Nhập tên vị trí"
                        readOnly={isHomeFlow || (!isAddmode && !isEditable)}
                        className={`h-[40px] rounded-xl border-gray-200 placeholder:text-gray-300 focus-visible:ring-[#e2a288] ${
                          isHomeFlow || (!isAddmode && !isEditable)
                            ? 'bg-gray-50 text-gray-700 selection:bg-transparent'
                            : 'bg-white'
                        }`}
                        onFocus={() => {
                          if (
                            isHomeFlow ||
                            isAddmode ||
                            !isEditable ||
                            !selectedPosition
                          )
                            return
                          editingPositionNameRef.current = {
                            id: selectedPosition.id,
                            name: selectedPosition.name,
                          }
                        }}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          if (isHomeFlow || (!isAddmode && !isEditable)) return
                          onPositionNameChange(e.target.value)
                        }}
                      />
                    </div>

                    {/* Tọa độ */}
                    <div className="flex flex-col gap-1.5">
                      <label
                        htmlFor="position-coords-input"
                        className="text-sm font-medium text-gray-700"
                      >
                        Tọa độ <span className="text-red-500">*</span>
                      </label>
                      <Input
                        id="position-coords-input"
                        value={
                          selectedPosition
                            ? `${(selectedPosition.rawX ?? selectedPosition.x).toFixed(5)}, ${(selectedPosition.rawY ?? selectedPosition.y).toFixed(5)}`
                            : 'Chưa có tọa độ'
                        }
                        readOnly
                        className="h-[40px] rounded-xl border-gray-200 bg-gray-50 text-gray-700 selection:bg-transparent"
                      />
                    </div>

                    {/* Hướng (Cộng thêm Yaw của Home) */}
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center justify-between">
                        <label
                          htmlFor="position-direction-input"
                          className="text-sm font-medium text-gray-700"
                        >
                          Hướng <span className="text-red-500">*</span>
                        </label>
                        {selectedPosition?.id === 'home' &&
                          !isHomePlacementRequired &&
                          !isEditable &&
                          onEditHomeDirection && (
                            <button
                              type="button"
                              onClick={onEditHomeDirection}
                              className="text-xs font-medium text-[#CE7A58] hover:underline"
                            >
                              Xoay hướng
                            </button>
                          )}
                      </div>
                      <Input
                        value={
                          calculatedYaw !== null
                            ? formatYawLabel(calculatedYaw)
                            : isAddmode
                              ? 'Bắc (0.00000)'
                              : 'Chưa có hướng'
                        }
                        readOnly
                        className="h-[40px] rounded-xl border-gray-200 bg-gray-50 text-gray-700 selection:bg-transparent"
                      />
                      {(isAddmode || isEditable) && (
                        <span className="text-xs italic text-gray-400 mt-0.5">
                          Xoay hướng trực tiếp trên sơ đồ.
                        </span>
                      )}
                    </div>
                  </div>
                ),
            },
            ...(showScheduleTab
              ? [
                  {
                    key: 'schedule',
                    label: 'Thời gian làm việc',
                    children: !selectedPosition ? (
                      <div className="rounded-xl border bg-[#f6f7fb] p-4 text-center text-gray-400 text-sm">
                        Chưa có vị trí nào
                      </div>
                    ) : (
                      <ScheduleListPanel
                        robotId={robotId}
                        locationId={selectedPosition.id || ''}
                        isActive={
                          activeTab === '2' && positionPanelTab === 'schedule'
                        }
                        schedules={selectedPosition.timeRanges}
                        isLoading={
                          !isAddmode &&
                          loadingSchedulePositionId === selectedPosition.id &&
                          !selectedPosition.scheduleLoaded
                        }
                        isAddmode={isAddmode}
                        onSchedulesChange={onSchedulesChange}
                        onLocalTimeRangesChange={onLocalTimeRangesChange}
                      />
                    ),
                  },
                ]
              : []),
          ]}
        />
      </div>

      {/* Nút hành động bao gồm nút Hủy cho cả flow Home */}
      {(isAddmode ||
        (selectedPosition && positionPanelTab === 'info' && isEditable) ||
        isHomePlacementRequired) && (
        <div className="mt-6 pt-2 flex items-center gap-3">
          <Button
            variant="outline"
            onClick={onCancelEdit}
            className="flex-1 h-11 rounded-full border-gray-200 text-gray-500 font-medium text-base shadow-sm hover:bg-gray-50 transition-colors"
          >
            Hủy
          </Button>
          <Button
            onClick={onSave}
            className="flex-1 h-11 rounded-full bg-[#CE7A58] hover:bg-[#b86747] text-white font-medium text-base shadow-sm transition-colors"
          >
            {isAddmode ? 'Thêm' : 'Lưu'}
          </Button>
        </div>
      )}
    </div>
  )
}
